import { FC, useMemo, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'

import { useAppDispatch, useAppSelector } from 'hooks/store'
import {
  CourseInviteProvider,
  CoursePaymentType,
  CourseProvider,
  CourseType
} from 'share/domain/course'
import { isDebtorType1 } from 'reducers/activecase/activecaseSlice'
import { setSelectedCourse } from 'reducers/activecourse/activecourseSlice'
import {
  CourseCardContent,
  CourseCardHeader,
  CourseCardPrice,
  StyledCard,
  StyledCardFooter
} from 'components/atoms/StyledCard'

import { getManualUploadCertData } from 'share/utils/courseUtils'

import { DocumentCategory } from 'share/domain/document'
import {
  setDocumentProcessing,
  getEmptyDocumentProcessing
} from 'reducers/document/documentProcessingSlice'
import FormattedPrice from 'components/atoms/FormattedPrice'
import { coursesDateFormat } from 'share/utils/dates'

import {
  CoursesProviderCardsWrapper,
  CoursesProviderFooter,
  CoursesProviderInfoText
} from './styles'

const firstTrusteeLink =
  'https://www.justice.gov/ust/list-credit-counseling-agencies-approved-pursuant-11-usc-111'

const secondTrusteeLink =
  'https://www.justice.gov/ust/list-approved-providers-personal-financial-management-instructional-courses-debtor-education'

const phoneDisclaimer = (
  <p data-testid='courses-phone-disclaimer'>
    *Phone course only available in English and Spanish
  </p>
)

interface CourseTabProviderDisplayProps {
  coursePaymentType: CoursePaymentType
  courseProviderList: CourseInviteProvider[]
}

interface History {
  businessObjectId?: number | null
  businessObjectType?: string
  caseId?: number
  coursePaymentType?: CoursePaymentType
  courseProviderList?: CourseInviteProvider[]
  navFromCourses?: boolean
  type?: string
}

const CourseTabProviderDisplay: FC<CourseTabProviderDisplayProps> = ({
  coursePaymentType,
  courseProviderList
}) => {
  const history = useHistory<History>()
  const dispatch = useAppDispatch()
  const caseData = useAppSelector((state) => state.activeCase)
  const courseData = useAppSelector((state) => state.activeCourse)
  const isDebtor1 = useAppSelector(isDebtorType1)

  const currentDate = useRef(coursesDateFormat(new Date().toISOString()))

  const isCricketCourse = useMemo(() => {
    if (coursePaymentType === CoursePaymentType.FirmPays) {
      return courseProviderList.some(
        ({ productKey }) => productKey === CourseProvider.CricketDebtCounseling
      )
    }

    return false
  }, [coursePaymentType, courseProviderList])

  function handleTakeCourseOnline(selectedCourse: CourseInviteProvider) {
    dispatch(setSelectedCourse(selectedCourse))
    history.push({
      pathname: '/dashboard/courses/confirm',
      state: {
        type: 'online',
        coursePaymentType,
        courseProviderList
      }
    })
  }

  function handleTakeCourseByPhone(selectedCourse: CourseInviteProvider) {
    console.log('handleTakeCourseByPhone ', selectedCourse.productKey)
    dispatch(setSelectedCourse(selectedCourse))
    history.push({
      pathname: '/dashboard/courses/confirm',
      state: {
        type: 'phone',
        coursePaymentType,
        courseProviderList
      }
    })
  }

  function getCertificateUploadObjectType(): string {
    let businessObjectType = 'order-'
    if (isDebtor1) {
      businessObjectType += courseData.activeTab + '-debtor1'
    } else {
      businessObjectType += courseData.activeTab + '-debtor2'
    }
    return businessObjectType
  }

  async function handleUploadCertificateClick() {
    const input = {
      caseId: caseData.matter?.id || 0,
      courseType:
        courseData.activeTab === 'first'
          ? CourseType.CreditCounseling
          : CourseType.DebtorEducation
    }

    try {
      const result = await getManualUploadCertData(input)

      if (result.data) {
        const data = result.data
        data.documents.manualUploadCertificateData.generalDocumentId

        const category: DocumentCategory = {
          id: data.documents.manualUploadCertificateData.generalDocumentId,
          name: data.documents.manualUploadCertificateData
            .certificateDocumentName,
          description:
            data.documents.manualUploadCertificateData
              .generalDocumentDescription,
          subcategoryList:
            data.documents.manualUploadCertificateData
              .generalDocumentSubcategoryList,
          status:
            data.documents.manualUploadCertificateData.generalDocumentStatus
        }

        const docProcessing = getEmptyDocumentProcessing()
        docProcessing.documentOperation = 'Submit'
        docProcessing.selectedDocumentCategoryId =
          data.documents.manualUploadCertificateData.certificateDocumentId
        docProcessing.selectedDocumentCategory = category
        docProcessing.isCourseCertificateUpload = true

        dispatch(setDocumentProcessing(docProcessing))

        const businessObjectType = getCertificateUploadObjectType()
        console.log('businessObjectType is', businessObjectType)

        history.push({
          pathname: '/dashboard/documents/subCategory',
          state: {
            navFromCourses: true,
            businessObjectId: null,
            businessObjectType: businessObjectType
          }
        })
      } else {
        console.log('error getting manual cert update data result.data is null')
      }
    } catch (error) {
      console.log('error getting manual cert update data with error ', error)
    }
  }

  const isFirstTab = courseData?.activeTab === 'first'

  return (
    <div data-testid='course-tab-choose-course-content'>
      {coursePaymentType === CoursePaymentType.ClientPays &&
        courseProviderList.length > 0 && (
          <CoursesProviderInfoText data-testid='course-tab-choose-course-clientpays-title'>
            Choose from the providers listed below
          </CoursesProviderInfoText>
        )}
      <CoursesProviderCardsWrapper data-testid='course-tab-choose-course-card-wrapper'>
        {courseProviderList.map((courseProvider, index) => (
          <StyledCard key={courseProvider.productKey}>
            <Card.Body>
              <CourseCardHeader>
                <h4
                  data-testid={`course-tab-choose-course-provider-name-${index}`}
                >
                  {courseProvider.name}
                </h4>
                <CourseCardPrice>
                  <p>Course Price</p>
                  {coursePaymentType === CoursePaymentType.ClientPays && (
                    <span
                      data-testid={`course-tab-choose-course-clientpays-price-${index}`}
                    >
                      <FormattedPrice price={courseProvider.price} />
                    </span>
                  )}
                  {coursePaymentType === CoursePaymentType.FirmPays && (
                    <span
                      className='long'
                      data-testid={`course-tab-choose-course-firmpays-price-${index}`}
                    >
                      Billed to Law Firm
                    </span>
                  )}
                </CourseCardPrice>
              </CourseCardHeader>
              {courseProvider.valueProps && (
                <CourseCardContent
                  dangerouslySetInnerHTML={{
                    __html: courseProvider.valueProps.toString()
                  }}
                  data-testid={`courses-course-value-props-${index}`}
                />
              )}
            </Card.Body>
            <StyledCardFooter>
              <Button
                data-testid={`courses-course-take-course-online-button-${index}`}
                variant='outline-secondary'
                onClick={() => handleTakeCourseOnline(courseProvider)}
              >
                Take Course Online
              </Button>
              <Button
                data-testid={`courses-course-take-course-by-phone-button-${index}`}
                variant='link'
                onClick={() => handleTakeCourseByPhone(courseProvider)}
              >
                Take Course By Phone Call
              </Button>
            </StyledCardFooter>
          </StyledCard>
        ))}
      </CoursesProviderCardsWrapper>
      <CoursesProviderFooter data-testid='courses-footer'>
        {coursePaymentType === CoursePaymentType.ClientPays &&
          courseProviderList.length > 0 && (
            <div data-testid='course-tab-choose-footer-1'>
              {isFirstTab && phoneDisclaimer}
              <p>
                All Providers listed above are approved to issue certificates
                evidencing completion of credit counseling in compliance with
                the Bankruptcy Code in all jurisdictions. Approval does not
                endorse or assure the quality of the Provider's services.
              </p>
              <p>
                Prices are current as of {currentDate.current} and are subject
                to change.
              </p>
            </div>
          )}
        {coursePaymentType === CoursePaymentType.ClientPays ? (
          <div data-testid='course-tab-choose-footer-2'>
            <p>
              To see a full list of US Trustee approved providers visit
              {' the: '}
              <a
                data-testid='courses-clientpays-trustee-link'
                href={!isFirstTab ? secondTrusteeLink : firstTrusteeLink}
                rel='noreferrer'
                target='_blank'
              >
                US Trustee office website
              </a>
              .
            </p>
            <p>
              Already have a course certificate
              {'? '}
              <Button
                data-testid='courses-clientpays-upload-certificate-link'
                className='ui-courses-footer-button-as-link'
                variant='link'
                onClick={handleUploadCertificateClick}
              >
                Upload it here
              </Button>
              .
            </p>
          </div>
        ) : (
          <div data-testid='course-tab-choose-footer-3'>
            {isCricketCourse && isFirstTab && phoneDisclaimer}
            <p>
              The provider listed above is approved to issue certificates
              evidencing completion of credit counseling in compliance with the
              Bankruptcy Code in all jurisdictions. Approval does not endorse or
              assure the quality of the Provider's services.
            </p>
          </div>
        )}
      </CoursesProviderFooter>
    </div>
  )
}

export default CourseTabProviderDisplay
